/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDarkMode } from "../../context/DarkModeContext";
import {
  SkeletonJobForm,
  SkeletonJobLoading,
  SkeletonJobError,
} from "../../components/Skeleton";

interface JobForm {
  title: string;
  company: string;
  location: string;
  salary: string;
  type: string;
  description: string;
  category: string;
  deadline: string;
  imageUrl?: string;
  image?: File;
  imagePreview?: string;
}

export default function EditJob() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDark } = useDarkMode();
  const [form, setForm] = useState<JobForm>({
    title: "",
    company: "",
    location: "",
    salary: "",
    type: "full_time",
    description: "",
    category: "",
    deadline: "",
  });

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);

  // Map human-readable labels to enum values
  const jobTypeOptions = [
    { value: "full_time", label: "Full-time" },
    { value: "part_time", label: "Part-time" },
    { value: "contract", label: "Contract" },
    { value: "freelance", label: "Freelance" },
    { value: "internship", label: "Internship" },
    { value: "remote", label: "Remote" },
    { value: "temporary", label: "Temporary" },
  ];

  const categoryOptions = [
    { value: "technology", label: "Technology" },
    { value: "healthcare", label: "Healthcare" },
    { value: "finance", label: "Finance" },
    { value: "education", label: "Education" },
    { value: "marketing", label: "Marketing" },
    { value: "sales", label: "Sales" },
    { value: "design", label: "Design" },
    { value: "engineering", label: "Engineering" },
    { value: "customer_service", label: "Customer Support" },
    { value: "human_resources", label: "Human Resources" },
    { value: "operations", label: "Operations" },
    { value: "legal", label: "Legal" },
    { value: "hospitality", label: "Hospitality" },
    { value: "construction", label: "Construction" },
    { value: "transportation", label: "Transportation" },
    { value: "retail", label: "Retail" },
    { value: "manufacturing", label: "Manufacturing" },
    { value: "agriculture", label: "Agriculture" },
    { value: "entertainment", label: "Entertainment" },
    { value: "other", label: "Other" },
  ];

  // Fetch job data when component mounts
  useEffect(() => {
    if (id) {
      fetchJobData();
    } else {
      setError("No job ID provided");
      setFetchLoading(false);
    }
  }, [id]);

  const fetchJobData = async () => {
    try {
      setFetchLoading(true);
      setError("");
      setNotFound(false);

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      console.log("Fetching job data for ID:", id);

      const API_BASE_URL = import.meta.env.VITE_API_BASE;

      const response = await fetch(`${API_BASE_URL}/jobs/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          setNotFound(true);
          throw new Error("Job not found");
        } else if (response.status === 401) {
          throw new Error("Authentication failed");
        } else {
          throw new Error(`Failed to fetch job: ${response.status}`);
        }
      }

      const jobData = await response.json();

      console.log("Fetched job data:", jobData);

      // Handle different possible data structures
      let actualJobData = jobData;

      // If data is nested under a job property
      if (jobData.job) {
        actualJobData = jobData.job;
        console.log("Using nested job data:", actualJobData);
      }

      // If data is in an array
      if (Array.isArray(jobData) && jobData.length > 0) {
        actualJobData = jobData[0];
        console.log("Using first array item:", actualJobData);
      }

      // Format deadline for date input (remove time part if exists)
      const deadline = actualJobData.deadline
        ? new Date(actualJobData.deadline).toISOString().split("T")[0]
        : "";

      // Populate form with the actual data
      const formData = {
        title: actualJobData.title || "",
        company: actualJobData.company || "",
        location: actualJobData.location || "",
        salary: actualJobData.salary || "",
        type: actualJobData.type || "full_time",
        description: actualJobData.description || "",
        category: actualJobData.category || "",
        deadline: deadline,
        imageUrl: actualJobData.imageUrl || actualJobData.image || "",
      };

      console.log("Form data to be set:", formData);

      setForm(formData);
    } catch (err: any) {
      console.error("Error fetching job:", err);
      setError(err.message || "Failed to load job data");

      if (!err.message.includes("not found")) {
        setTimeout(() => {
          navigate("/dashboard/admin-panel", { state: { activeTab: "jobs" } });
        }, 3000);
      }
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setError("Please select a valid image file");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError("Image size should be less than 5MB");
        return;
      }

      setError("");

      const previewUrl = URL.createObjectURL(file);
      setForm((prev) => ({
        ...prev,
        image: file,
        imagePreview: previewUrl,
      }));
    }
  };

  const removeImage = () => {
    if (form.imagePreview) {
      URL.revokeObjectURL(form.imagePreview);
    }
    setForm((prev) => ({
      ...prev,
      image: undefined,
      imagePreview: undefined,
      imageUrl: undefined,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Validate required fields
      if (
        !form.title ||
        !form.company ||
        !form.location ||
        !form.salary ||
        !form.description ||
        !form.category
      ) {
        throw new Error("Please fill in all required fields");
      }

      // Validate deadline
      if (form.deadline) {
        const deadlineDate = new Date(form.deadline);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (deadlineDate < today) {
          throw new Error("Deadline cannot be in the past");
        }
      }

      // Create JSON payload
      const payload = {
        title: form.title,
        company: form.company,
        location: form.location,
        salary: form.salary,
        type: form.type,
        description: form.description,
        category: form.category,
        deadline: form.deadline || null,
      };

      console.log("Sending update payload:", payload);

      const API_BASE_URL = import.meta.env.VITE_API_BASE;

      const response = await fetch(`${API_BASE_URL}/jobs/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error ||
            errorData.message ||
            `Failed to update job: ${response.status}`,
        );
      }

      const data = await response.json();
      console.log("Job updated successfully:", data);

      // Handle image upload if a new image was selected
      if (form.image && data.job && data.job.id) {
        await uploadJobImage(data.job.id, form.image, token);
      }

      // Clean up preview URL
      if (form.imagePreview) {
        URL.revokeObjectURL(form.imagePreview);
      }

      // Show success message and redirect
      alert("Job updated successfully!");
      navigate("/dashboard/admin-panel", { state: { activeTab: "jobs" } });
    } catch (err: any) {
      console.error("Error updating job:", err);
      setError(err.message || "An error occurred while updating the job");
    } finally {
      setLoading(false);
    }
  };

  const uploadJobImage = async (jobId: string, image: File, token: string) => {
    try {
      const imageFormData = new FormData();
      imageFormData.append("image", image);

      const API_BASE_URL = import.meta.env.VITE_API_BASE;

      const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/image`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: imageFormData,
      });

      if (!response.ok) {
        console.warn(
          "Failed to upload image, but job was updated successfully",
        );
      } else {
        console.log("Image uploaded successfully");
      }
    } catch (error) {
      console.warn("Error uploading image:", error);
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Loading state
  if (fetchLoading) {
    return (
      <SkeletonJobLoading
        theme={isDark ? "dark" : "light"}
        // message="Loading job data..."
      />
    );
  }

  // Not found state
  if (notFound) {
    return (
      <div
        className={`min-h-screen p-4 sm:p-6 ${
          isDark
            ? "bg-gradient-to-br from-gray-900 to-gray-800"
            : "bg-gradient-to-br from-green-50 to-sky-50"
        }`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div
              className={`border rounded-lg max-w-md mx-auto p-4 ${
                isDark
                  ? "bg-red-900/20 border-red-700 text-red-300"
                  : "bg-red-100 border-red-400 text-red-700"
              }`}
            >
              <h2 className="text-xl font-semibold mb-2">Job Not Found</h2>
              <p className="mb-4">
                The job you're trying to edit doesn't exist or you don't have
                permission to edit it.
              </p>
              <button
                onClick={() =>
                  navigate("/dashboard/admin-panel", {
                    state: { activeTab: "jobs" },
                  })
                }
                className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 transition-colors"
              >
                Back to Jobs
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state - Show skeleton if no form data
  if (error && !form.title) {
    return (
      <SkeletonJobError
        theme={isDark ? "dark" : "light"}
        // title="Error Loading Job"
        // message={error}
      />
    );
  }

  // Loading state for form (when form is empty but not fetching)
  if (!fetchLoading && !form.title && !error) {
    return <SkeletonJobForm theme={isDark ? "dark" : "light"} mode="edit" />;
  }

  return (
    <div
      className={`min-h-screen p-4 sm:p-6 ${
        isDark
          ? "bg-gradient-to-br from-gray-900 to-gray-800"
          : "bg-gradient-to-br from-green-50 to-sky-50"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-green-600 to-sky-500 bg-clip-text text-transparent mb-2">
            Edit Job
          </h1>
          <p
            className={`text-sm sm:text-base ${
              isDark ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Editing: {form.title || "Loading..."}
          </p>
          {id && (
            <p
              className={`text-sm mt-1 ${
                isDark ? "text-gray-500" : "text-gray-500"
              }`}
            >
              Job ID: {id}
            </p>
          )}
        </div>

        {/* Form Card */}
        <div
          className={`shadow-lg rounded-xl sm:rounded-2xl overflow-hidden border ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-green-100"
          }`}
        >
          <div className="px-4 sm:px-8 py-4 sm:py-6 bg-gradient-to-r from-green-500 to-sky-500">
            <h2 className="text-lg sm:text-xl font-semibold text-white text-center sm:text-left">
              Edit Job Details
            </h2>
          </div>

          <div className="p-4 sm:p-6 lg:p-8">
            {error && (
              <div
                className={`mb-4 sm:mb-6 p-3 sm:p-4 rounded-lg border text-sm sm:text-base ${
                  isDark
                    ? "bg-red-900/20 border-red-700 text-red-300"
                    : "bg-red-100 border-red-200 text-red-700"
                }`}
              >
                <strong>Error:</strong> {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
              {/* Image Upload Section */}
              <div>
                <label
                  className={`block text-sm sm:text-base font-medium mb-2 sm:mb-3 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Job Poster/Image (Optional)
                </label>
                <p
                  className={`text-xs sm:text-sm mb-3 sm:mb-4 ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {form.imageUrl
                    ? "Current image will be replaced if you upload a new one"
                    : "Upload an image for this job posting"}
                </p>

                {form.imagePreview ? (
                  <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
                    <div className="relative">
                      <img
                        src={form.imagePreview}
                        alt="New job poster preview"
                        className={`w-40 h-28 sm:w-48 sm:h-32 object-cover rounded-lg border-2 ${
                          isDark ? "border-green-600" : "border-green-200"
                        }`}
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        aria-label="Remove image"
                      >
                        <svg
                          className="w-3 h-3 sm:w-4 sm:h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                    <p
                      className={`text-sm ${
                        isDark ? "text-green-400" : "text-green-600"
                      }`}
                    >
                      New image selected (will replace current image) ✓
                    </p>
                  </div>
                ) : form.imageUrl ? (
                  <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
                    <div className="relative">
                      <img
                        src={form.imageUrl}
                        alt="Current job poster"
                        className={`w-40 h-28 sm:w-48 sm:h-32 object-cover rounded-lg border-2 ${
                          isDark ? "border-green-600" : "border-green-200"
                        }`}
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        title="Remove current image"
                        aria-label="Remove current image"
                      >
                        <svg
                          className="w-3 h-3 sm:w-4 sm:h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                    <p
                      className={`text-sm ${
                        isDark ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Current image (tap X to remove)
                    </p>
                  </div>
                ) : (
                  <div
                    className={`border-2 border-dashed rounded-lg p-4 sm:p-6 hover:border-green-300 transition-colors w-full max-w-md mx-auto ${
                      isDark
                        ? "border-green-600 hover:border-green-500 bg-gray-700/50"
                        : "border-green-200 hover:border-green-300"
                    }`}
                  >
                    <svg
                      className={`mx-auto h-10 w-10 sm:h-12 sm:w-12 ${
                        isDark ? "text-gray-500" : "text-gray-400"
                      }`}
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="mt-3 sm:mt-4 flex text-sm">
                      <label
                        className={`relative cursor-pointer rounded-md font-medium focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-green-500 mx-auto text-center ${
                          isDark
                            ? "text-green-400 hover:text-green-300 bg-gray-800"
                            : "text-green-600 hover:text-green-500 bg-white"
                        }`}
                      >
                        <span className="text-sm sm:text-base">
                          Upload an image
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="sr-only"
                        />
                      </label>
                    </div>
                    <p
                      className={`text-xs mt-2 text-center ${
                        isDark ? "text-gray-500" : "text-gray-500"
                      }`}
                    >
                      PNG, JPG, GIF up to 5MB
                    </p>
                  </div>
                )}
              </div>

              {/* Form Fields */}
              <div className="space-y-4 sm:space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  <div className="md:col-span-2 lg:col-span-1">
                    <label
                      className={`block text-sm sm:text-base font-medium mb-1 sm:mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Job Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      placeholder="e.g., Senior Frontend Developer"
                      value={form.title}
                      onChange={handleChange}
                      required
                      className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-200 ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "border-green-200 text-gray-900"
                      }`}
                    />
                  </div>

                  <div className="md:col-span-2 lg:col-span-1">
                    <label
                      className={`block text-sm sm:text-base font-medium mb-1 sm:mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Company Name *
                    </label>
                    <input
                      type="text"
                      name="company"
                      placeholder="e.g., Tech Solutions Inc."
                      value={form.company}
                      onChange={handleChange}
                      required
                      className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-200 ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "border-green-200 text-gray-900"
                      }`}
                    />
                  </div>

                  <div className="md:col-span-2 lg:col-span-1">
                    <label
                      className={`block text-sm sm:text-base font-medium mb-1 sm:mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Location *
                    </label>
                    <input
                      type="text"
                      name="location"
                      placeholder="e.g., Nairobi, Kenya or Remote"
                      value={form.location}
                      onChange={handleChange}
                      required
                      className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-200 ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "border-green-200 text-gray-900"
                      }`}
                    />
                  </div>
                </div>

                {/* Job Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  <div>
                    <label
                      className={`block text-sm sm:text-base font-medium mb-1 sm:mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Salary *
                    </label>
                    <input
                      type="text"
                      name="salary"
                      placeholder="e.g., KES 80,000 / month"
                      value={form.salary}
                      onChange={handleChange}
                      required
                      className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-200 ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "border-green-200 text-gray-900"
                      }`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm sm:text-base font-medium mb-1 sm:mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Job Type *
                    </label>
                    <select
                      name="type"
                      value={form.type}
                      onChange={handleChange}
                      className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-200 ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "border-green-200 text-gray-900"
                      }`}
                    >
                      {jobTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      className={`block text-sm sm:text-base font-medium mb-1 sm:mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Category *
                    </label>
                    <select
                      name="category"
                      value={form.category}
                      onChange={handleChange}
                      required
                      className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-200 ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "border-green-200 text-gray-900"
                      }`}
                    >
                      <option value="">Select a category</option>
                      {categoryOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Deadline */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label
                      className={`block text-sm sm:text-base font-medium mb-1 sm:mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Application Deadline (Optional)
                    </label>
                    <input
                      type="date"
                      name="deadline"
                      value={form.deadline}
                      onChange={handleChange}
                      min={getTodayDate()}
                      className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-200 ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "border-green-200 text-gray-900"
                      }`}
                    />
                    <p
                      className={`text-xs sm:text-sm mt-1 ${
                        isDark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Leave empty if there's no specific deadline
                    </p>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label
                    className={`block text-sm sm:text-base font-medium mb-1 sm:mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Job Description *
                  </label>
                  <textarea
                    name="description"
                    placeholder="Enter detailed job description, requirements, responsibilities..."
                    value={form.description}
                    onChange={handleChange}
                    required
                    rows={4}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-200 resize-vertical ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "border-green-200 text-gray-900"
                    }`}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-4 sm:pt-6">
                <button
                  type="button"
                  onClick={() => {
                    if (form.imagePreview) {
                      URL.revokeObjectURL(form.imagePreview);
                    }
                    navigate("/dashboard/admin-panel", {
                      state: { activeTab: "jobs" },
                    });
                  }}
                  className={`flex-1 py-3 sm:py-4 px-4 sm:px-6 border font-medium rounded-lg transition-all duration-300 shadow-sm text-base sm:text-lg order-2 sm:order-1 ${
                    isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-3 sm:py-4 px-4 sm:px-6 bg-gradient-to-r from-green-500 to-sky-500 text-white font-medium rounded-lg hover:from-green-600 hover:to-sky-600 disabled:opacity-70 transition-all duration-300 shadow-md hover:shadow-lg text-base sm:text-lg order-1 sm:order-2"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 sm:mr-3 h-4 w-4 sm:h-5 sm:w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Updating Job...
                    </span>
                  ) : (
                    "Update Job"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Quick Tips */}
        <div className="mt-6 sm:mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          <div
            className={`rounded-lg border p-4 sm:p-6 ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-green-200"
            }`}
          >
            <h3
              className={`text-base sm:text-lg font-semibold mb-2 sm:mb-3 ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Editing Tips
            </h3>
            <ul
              className={`text-xs sm:text-sm space-y-1 sm:space-y-2 ${
                isDark ? "text-gray-300" : "text-gray-600"
              }`}
            >
              <li>• Review all information for accuracy</li>
              <li>• Update salary if there have been changes</li>
              <li>• Extend deadline if needed to get more applicants</li>
              <li>• Improve description based on applicant feedback</li>
              <li>• Update location if the position has changed</li>
              <li>• Refresh the image to attract more attention</li>
            </ul>
          </div>

          <div
            className={`rounded-lg border p-4 sm:p-6 ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-green-200"
            }`}
          >
            <h3
              className={`text-base sm:text-lg font-semibold mb-2 sm:mb-3 ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Update Best Practices
            </h3>
            <ul
              className={`text-xs sm:text-sm space-y-1 sm:space-y-2 ${
                isDark ? "text-gray-300" : "text-gray-600"
              }`}
            >
              <li>• Keep job requirements up to date</li>
              <li>• Mention if the position is still available</li>
              <li>• Update contact information if changed</li>
              <li>• Add new benefits or perks</li>
              <li>• Include recent company updates</li>
              <li>• Refresh keywords for better visibility</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
